/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useState, type ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { PLACEHOLDER_IMAGES } from '@sportninja/common/constants/placeholderImages'
import { resolveAssetUrl } from '@sportninja/common/utils/assetUrl'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { isTeamNode } from '@sportninja/common/features/competition/operations/utils/nodeTypes'
import type { OperationsNode } from '@sportninja/common/features/competition/operations/types/operations.types'
import { heading5, paragraphXSmall, weightBold } from 'src/components/css'
import EntityIdChip from './EntityIdChip'
import PanelBreadcrumb from './PanelBreadcrumb'
import { teamRosterHref } from '../../utils/teamRosterHref'
import PanelStatStrip, { type PanelStat } from './PanelStatStrip'

export interface PanelOrganization {
  id: string
  name: string
}

interface PanelHeaderProps {
  node: OperationsNode
  ancestors: OperationsNode[]
  organization: PanelOrganization | null
  stats: PanelStat[]
  pills: ReactNode
  /** Line under the crest and title, e.g. the roster lock date. Omitted when empty. */
  meta?: ReactNode
  /** First action row: Move Team and Edit. */
  actions: ReactNode
  /** Second action row: Add Player, Import, Export. Omitted when empty. */
  secondaryActions?: ReactNode
  onSelectNode: (nodeId: string) => void
  /** The competition the page is showing — scopes a team's roster link. */
  rootScheduleId: string
}

const PanelHeader = ({
  node,
  ancestors,
  organization,
  stats,
  pills,
  meta,
  actions,
  secondaryActions,
  onSelectNode,
  rootScheduleId,
}: PanelHeaderProps) => {
  'use memo'

  const isTeam = isTeamNode(node)
  // Both shapes go through the resolver: a team's plain URL string and a
  // schedule's `{ full_path }` object.
  const logo = resolveAssetUrl(node.image)

  // Keyed by node id: the panel is NOT remounted per selection, so without
  // this a team whose crest 404s would leave the next team on the placeholder.
  const [failedLogoId, setFailedLogoId] = useState<string | null>(null)
  const logoFailed = failedLogoId === node.id
  // Every node shows a crest image: its own logo, or the shared placeholder
  // for its kind when it has none or when its logo fails to load. Schedule
  // nodes (season/conference/division) all read as "a competition".
  const fallback = isTeam
    ? PLACEHOLDER_IMAGES.team
    : PLACEHOLDER_IMAGES.competition
  const crestSrc = logoFailed ? fallback : logo ?? fallback

  return (
    <header css={headerStyle}>
      {/*
       * Two columns. Identity (breadcrumb, crest, title, pills) on the left;
       * UID chips and action buttons stacked on the right. Splitting this way
       * means an absent breadcrumb — a season or conference has no ancestors
       * left to show — simply shortens the left column rather than leaving a
       * reserved empty row across the whole header.
       */}
      <div css={topRowStyle}>
        <div css={identityColumnStyle}>
          <PanelBreadcrumb
            ancestors={ancestors}
            current={node}
            onSelectNode={onSelectNode}
          />

          <div css={titleRowStyle}>
            {/* Always an image: a node with no logo of its own gets the
                placeholder for its kind. `onError` catches a logo URL that
                resolves but 404s, which would otherwise leave an empty box. */}
            <img
              css={crestStyle(isTeam)}
              src={crestSrc}
              alt=''
              onError={() => setFailedLogoId(node.id)}
            />

            <div css={titlesStyle}>
              {/* A team's title opens its Roster tab for THIS competition —
                  the same link as the legacy tab's "Open in new tab". Opened in
                  a new tab so the operations panel and its tree selection stay
                  put. Schedule nodes have no such standalone page, so they stay
                  plain text. */}
              {isTeam ? (
                <a
                  css={[titleStyle, titleLinkStyle]}
                  href={teamRosterHref(node.teamId, rootScheduleId)}
                  target='_blank'
                  rel='noopener noreferrer'
                >
                  {capitalizeWords(node.name)}
                </a>
              ) : (
                <h2 css={titleStyle}>{capitalizeWords(node.name)}</h2>
              )}
              <div css={subStyle}>{pills}</div>
            </div>
          </div>

          {/* Starts at the crest's left edge, below the title row. */}
          {meta && <div css={subStyle}>{meta}</div>}
        </div>

        <div css={actionsColumnStyle}>
          {/* Every node carries the UIDs the legacy Setup tab exposed — support
              staff copy them straight out of the header rather than opening the
              edit dialog. Teams show org + team; a season / conference /
              division shows its own schedule id, plus the Salesforce id Canlan
              carries over from its legacy system. */}
          <div css={idsStyle}>
            {isTeamNode(node) ? (
              <>
                {organization && (
                  <EntityIdChip kind='organization' id={organization.id} />
                )}
                <EntityIdChip kind='team' id={node.teamId} />
              </>
            ) : (
              <>
                {/* Every schedule depth shares one noun, as legacy's
                    `EditEntitySubTitle` does — "SN Competition ID" on
                    SportNinja, "Canlan Season ID" on Canlan. */}
                <EntityIdChip kind='competition' id={node.scheduleId} />
                {isCanlan && node.salesforceId ? (
                  <EntityIdChip kind='salesforce' id={node.salesforceId} />
                ) : null}
              </>
            )}
          </div>
          {/* The two action rows are one group, tighter than the gap that
              separates them from the UID chips above. */}
          <div css={actionRowsStyle}>
            <div css={actionsStyle}>{actions}</div>
            {secondaryActions && (
              <div css={actionsStyle}>{secondaryActions}</div>
            )}
          </div>
        </div>
      </div>

      <PanelStatStrip stats={stats} />
    </header>
  )
}

const headerStyle = css`
  padding: 16px 20px 0;
  border-bottom: 1px solid ${colors.SECONDARY_200};
`

/* No min-height: the row is only as tall as whichever column is taller, so a
   node with no breadcrumb and no UID chips reserves no empty space. */
const topRowStyle = css`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 14px;
`

const identityColumnStyle = css`
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
  min-width: 260px;
`

const titleRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  flex-wrap: wrap;
`

const crestStyle = (isTeam: boolean) => css`
  ${weightBold}
  width: 52px;
  height: 52px;
  flex: none;
  border-radius: ${isTeam ? '50%' : '8px'};
  background: ${colors.SECONDARY_300};
  border: 1px solid ${colors.SECONDARY_200};
  display: flex;
  align-items: center;
  justify-content: center;
  /* contain, not cover — crests are often non-square and must not be cropped */
  object-fit: contain;
  padding: 3px;
  font-size: 17px;
  color: ${colors.NEUTRAL_50};
`

const titlesStyle = css`
  flex: 1;
  min-width: 200px;
`

const titleStyle = css`
  ${heading5}
  ${weightBold}
  margin: 0 0 5px;
  color: ${colors.NEUTRAL_0};
  text-wrap: balance;
`

/* The team title reuses `titleStyle` for size/weight, then reads as a link:
   inherits the heading colour at rest, and only reveals it is clickable on
   hover/focus so it does not shout against the schedule titles beside it. */
const titleLinkStyle = css`
  display: inline-block;
  cursor: pointer;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: ${colors.PRIMARY};
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid ${colors.PRIMARY};
    outline-offset: 2px;
  }
`

const subStyle = css`
  ${paragraphXSmall}
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  color: ${colors.NEUTRAL_100};
`

const actionsColumnStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  flex: none;
`

/* Stacked, not side by side: the two chips have different label widths, and
   stacking lets their copy buttons line up on the right edge. */
const idsStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`

const actionRowsStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
`

const actionsStyle = css`
  display: flex;
  gap: 8px;
  flex: none;
  flex-wrap: wrap;
  justify-content: flex-end;
`

export default PanelHeader
